import { ValidationRule } from 'react-hook-form';
import ManufactureSvg from 'src/assets/icons/manufacture.svg';
import { ModuleDtoCameraPositionEnum } from 'src/generated/rest-api/types';
import { z } from 'zod';

const envZodSchema = z.object({
  VITE_APP_ENVIRONMENT: z.string().optional(),
  VITE_GENERATED_VIDEOS_URL: z.string(),
  VITE_GIT_COMMIT: z.string().optional(),
  VITE_GIT_REF: z.string().optional(),
  VITE_GOOGLE_ANALYTICS_ID: z.string().optional(),
  VITE_REST_API_URL: z.string(),
  VITE_STREAMING_SERVER_URL: z.string(),
  VITE_STREAMING_SERVER_VIEWER_TOKEN: z.string(),
  VITE_VAPID_PUBLIC_KEY: z.string(),
  VITE_WEBSOCKETS_API_URL: z.string(),
});

const {
  VITE_APP_ENVIRONMENT,
  VITE_GENERATED_VIDEOS_URL,
  VITE_GIT_COMMIT,
  VITE_GIT_REF,
  VITE_GOOGLE_ANALYTICS_ID,
  VITE_REST_API_URL,
  VITE_STREAMING_SERVER_URL,
  VITE_STREAMING_SERVER_VIEWER_TOKEN,
  VITE_VAPID_PUBLIC_KEY,
  VITE_WEBSOCKETS_API_URL,
} = envZodSchema.parse(import.meta.env);

export const ENV_VARIABLES = {
  APP_ENVIRONMENT: VITE_APP_ENVIRONMENT,
  GENERATED_VIDEOS_URL: VITE_GENERATED_VIDEOS_URL,
  GIT_COMMIT: VITE_GIT_COMMIT,
  GIT_REF: VITE_GIT_REF,
  GOOGLE_ANALYTICS_ID: VITE_GOOGLE_ANALYTICS_ID,
  REST_API_URL: VITE_REST_API_URL,
  STREAMING_SERVER_URL: VITE_STREAMING_SERVER_URL,
  STREAMING_SERVER_VIEWER_TOKEN: VITE_STREAMING_SERVER_VIEWER_TOKEN,
  VAPID_PUBLIC_KEY: VITE_VAPID_PUBLIC_KEY,
  WEBSOCKETS_API_URL: VITE_WEBSOCKETS_API_URL,
};

if (!ENV_VARIABLES.GOOGLE_ANALYTICS_ID) {
  console.warn('environment variable VITE_GOOGLE_ANALYTICS_ID not found!');
}

export enum PUBLIC_ROUTES {
  LOGIN = '/login',

  SINGLE_QUALICAM = '/qualicam/:qualicamName',
}

export enum PRIVATE_ROUTES {
  ADMIN_USERS = '/admin-users',
  ALL_CAMERAS = '/all-cameras',
  DASHBOARD = '/dashboard',
  DASHBOARD_MODULE = '/dashboard/module',
  LEARNING = '/learning',
  MODULE = '/modules/:moduleId',
  MODULES = '/modules',
  MODULES_OVERVIEW = '/modules/overview',
  NOTIFICATION_LOG = '/admin-notification-log',
  QUALICAM_OVERVIEW = '/qualicam-overview',
  QUALICAM_OVERVIEW_SINGLE = '/qualicam-overview/:qualicamName',
  ROOT = '/',
  RUNS = '/runs',
  RUNS_SUMMARY = '/runs/summary',
  SETTINGS = '/settings',
  VIDEO_JOBS = '/admin-video-jobs',
}

export const DEFAULT_PAGINATION_PAGE_SIZE = 20;

export const EMAIL_REGEX_PATTERN: ValidationRule<RegExp> =
  /^(?![_.-])(?!.*[_.-]{2})[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const PHONE_NUMBER_REGEX_PATTERN: ValidationRule<RegExp> =
  /^\+?[1-9]\d{1,14}$/;

export const DEFAULT_MACHINE_ICON = ManufactureSvg;

export enum HTTP_ERROR_STATUS_CODES {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
}

export enum SOCKET_MESSAGE_TYPES {
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  EVENT_ADMIN_NOTIFICATION_CREATED = 'EVENT:ADMIN_NOTIFICATION_CREATED',
  EVENT_DETAILED_HEARTBEAT_DATA = 'EVENT:DETAILED_HEARTBEAT_DATA',
  EVENT_DIAGNOSTICS_DATA = 'EVENT:DIAGNOSTICS',
  EVENT_FLOW_CONTROL_METRICS_DATA = 'EVENT:FLOW_CONTROL_METRICS_DATA',
  EVENT_HMI_DATA = 'EVENT:HMI_DATA',
  EVENT_HMI_PERFORMANCE_METRICS_DATA = 'EVENT:PERFORMANCE_METRICS_DATA',
  EVENT_HMI_PROGRAM_DATA = 'EVENT:HMI_PROGRAM_DETAILS',
  EVENT_META_STATUS_DATA = 'EVENT:META_STATUS_DATA',
  EVENT_MODULE_UPDATED = 'EVENT:MODULE_UPDATED',
  EVENT_VIDEO_JOB_DELETED = 'EVENT:VIDEO_JOB_DELETED',
  EVENT_VIDEO_JOB_UPDATED = 'EVENT:VIDEO_JOB_UPDATED',
  EXCEPTION = 'exception',
  REQUEST_JOIN_ADMIN_ONLY_ROOM = 'REQUEST:JOIN_ADMIN_ONLY_ROOM',
  REQUEST_JOIN_MODULE_EVENTS_ROOM = 'REQUEST:JOIN_MODULE_EVENTS_ROOM',
  REQUEST_JOIN_NOTIFICATIONS_ROOM = 'REQUEST:JOIN_NOTIFICATIONS_ROOM',
  REQUEST_LEAVE_ADMIN_ONLY_ROOM = 'REQUEST:LEAVE_ADMIN_ONLY_ROOM',
  REQUEST_LEAVE_MODULE_EVENTS_ROOM = 'REQUEST:LEAVE_MODULE_EVENTS_ROOM',
  REQUEST_LEAVE_NOTIFICATIONS_ROOM = 'REQUEST:LEAVE_NOTIFICATIONS_ROOM',
  REQUEST_VIDEO_STREAMS = 'REQUEST:VIDEO_STREAMS',
  RESPONSE_VIDEO_STREAMS = 'RESPONSE:VIDEO_STREAMS',
  RTC_ANSWER = 'RTC_ANSWER',
  RTC_OFFER = 'RTC_OFFER',
}

export const DASHBOARD_CAMERA_ORDER: (
  | ModuleDtoCameraPositionEnum
  | undefined
)[] = [
  ModuleDtoCameraPositionEnum.Bunker,
  ModuleDtoCameraPositionEnum.BoxFiller,
  ModuleDtoCameraPositionEnum.SizeGrader,
  ModuleDtoCameraPositionEnum.Farmsort,
  undefined,
];
